/**
 * LE CADRAGE DE L'ATELIER — pour qui, et sur quoi.
 *
 * Le fondateur, 14 septembre 2026 : « On doit pouvoir choisir le niveau de
 * l'élève ainsi que les savoirs visés lors de la conception des travaux. » Et,
 * le même jour : « n'oublie pas qu'en 4e et 5e sec il y a un choix de séquence
 * en math. »
 *
 * Rien ne se conçoit dans le vide. L'atelier ouvre sur l'année, puis la
 * séquence quand elle s'applique, puis le savoir — dont découlent la matière,
 * les notions et les erreurs types qu'un leurre pourra pointer. Le professeur
 * ne saisit ni la matière ni l'année de l'exercice : elles viennent du savoir,
 * donc il ne peut pas se tromper.
 *
 * LA RÈGLE DE SÉQUENCE N'EST PAS REDITE ICI. `savoirConvientA` vit dans
 * `@silex/shared` et décide partout où un savoir est choisi pour un élève ;
 * l'atelier l'appelle plutôt que d'en écrire une seconde, qui divergerait.
 */
import { savoirConvientA, type SequenceMaths } from '@silex/shared';

/** Un savoir tel que `GET /v1/corpus/savoirs` le rend. */
export interface SavoirCadrage {
  readonly id: string;
  readonly code: string;
  readonly enonce: string;
  readonly matiere: string;
  readonly niveau: string;
  readonly sequences: readonly string[];
  readonly notions: readonly { id: string; code: string; titre: string; ordre: number }[];
}

/** Les années où le programme de mathématiques se scinde en séquences. */
export const NIVEAUX_A_SEQUENCE = ['sec4', 'sec5'] as const;

/**
 * La séquence a-t-elle un sens pour ce cadrage ?
 *
 * En français, jamais. De la 1re à la 3e, jamais non plus : le programme n'y
 * est pas scindé. Poser la question là où elle n'a pas lieu d'être ferait
 * croire à un choix qui n'existe pas.
 */
export function sequenceApplicable(matiere: string, niveau: string): boolean {
  return matiere === 'mathematiques' && (NIVEAUX_A_SEQUENCE as readonly string[]).includes(niveau);
}

/**
 * Les savoirs qu'on peut viser pour cette année, cette matière et cette
 * séquence — dans l'ordre du programme, tel que l'API le rend.
 *
 * Un savoir sans séquence est commun à toutes : c'est la règle partagée, et
 * c'est pourquoi un filtre vide ne retire jamais rien.
 */
export function savoirsDuCadrage(
  savoirs: readonly SavoirCadrage[],
  cadre: { matiere: string; niveau: string; sequence: SequenceMaths | null },
): readonly SavoirCadrage[] {
  return savoirs.filter(
    (s) =>
      s.matiere === cadre.matiere &&
      s.niveau === cadre.niveau &&
      savoirConvientA(
        s.sequences,
        sequenceApplicable(cadre.matiere, cadre.niveau) ? cadre.sequence : null,
      ),
  );
}

/**
 * Les notions d'un savoir, dans l'ordre d'ENSEIGNEMENT — 1 avant 2 avant 3 —,
 * jamais celui de l'alphabet. Un professeur relit et conçoit dans l'ordre où
 * l'élève rencontre les choses.
 */
export function notionsOrdonnees(
  savoir: SavoirCadrage | undefined,
): readonly { id: string; code: string; titre: string; ordre: number }[] {
  return [...(savoir?.notions ?? [])].sort(
    (a, b) => a.ordre - b.ordre || a.code.localeCompare(b.code),
  );
}
